import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const pricingTiers = [
  {
    name: "Complete Fall Cleanup",
    price: "Starting at $479",
    description: "For homeowners who want the entire property, including the gutters, prepared for winter.",
    included: [
      "Front-yard leaf cleanup",
      "Backyard leaf cleanup",
      "Leaves collected and hauled away",
      "Gutters cleared of leaves and debris",
      "Final blow-off of serviced areas",
    ],
    featured: false,
  },
  {
    name: "Full Property Cleanup",
    price: "Starting at $349",
    description: "Our complete ground-level leaf cleanup for a clean, finished property from front to back.",
    included: [
      "Front-yard leaf cleanup",
      "Backyard leaf cleanup",
      "Leaves collected and hauled away",
      "Final blow-off of serviced areas",
    ],
    featured: true,
  },
  {
    name: "Front Yard Cleanup",
    price: "Starting at $299",
    description: "A straightforward option for restoring your home's curb appeal without a complete property cleanup.",
    included: [
      "Front yard only",
      "Leaves collected and hauled away",
      "Driveway and front walkways blown clean",
      "Final blow-off of serviced areas",
    ],
    featured: false,
  },
];

export default function PricingSection({ showLabel = true }) {
  return (
    <section className="bg-white" data-testid="pricing-section">
      <div className="mx-auto max-w-[1160px] px-5 py-16 md:px-8 md:py-24">
        <Reveal>
          <div className="text-center">
            {showLabel && <Eyebrow>Fall Leaf Cleanup Packages</Eyebrow>}
            <h2 className="mx-auto mt-4 max-w-2xl text-3xl font-extrabold leading-[1.15] tracking-tight text-[#16241C] md:text-[2.4rem]">
              Choose Your Fall Cleanup
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-[1.6] text-[#5B6B60]">
              A straightforward service level for every property, from curb appeal to complete seasonal cleanup.
            </p>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-3 lg:gap-8">
          {pricingTiers.map((tier, i) => (
            <Reveal key={tier.name} delay={i * 0.08}>
              <div
                data-testid={`pricing-card-${i}`}
                className={`relative flex h-full flex-col rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1.5 ${
                  tier.featured
                    ? "bg-[#043B1C] shadow-[0_24px_60px_-24px_rgba(4,59,28,0.55)] lg:-translate-y-3 lg:hover:-translate-y-5"
                    : "border border-[#16241C]/10 bg-white hover:shadow-[0_20px_50px_-20px_rgba(4,59,28,0.35)]"
                }`}
              >
                {tier.featured && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-[#F4BE36] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#043B1C]" data-testid="pricing-popular-badge">
                    Most Popular
                  </span>
                )}
                <h3 className={`text-xl font-extrabold tracking-tight ${tier.featured ? "text-white" : "text-[#16241C]"}`}>
                  {tier.name}
                </h3>
                <p className={`mt-3 text-3xl font-extrabold tracking-tight ${tier.featured ? "text-[#F4BE36]" : "text-[#043B1C]"}`} data-testid={`pricing-price-${i}`}>
                  {tier.price}
                </p>
                <p className={`mt-3 text-sm leading-[1.6] ${tier.featured ? "text-white/70" : "text-[#5B6B60]"}`}>
                  {tier.description}
                </p>
                <ul className="mt-6 flex-1 space-y-3">
                  {tier.included.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${tier.featured ? "bg-[#F4BE36]" : "bg-[#043B1C]"}`}>
                        <Check size={11} strokeWidth={3.5} className={tier.featured ? "text-[#043B1C]" : "text-[#F4BE36]"} />
                      </span>
                      <span className={`text-sm leading-[1.5] ${tier.featured ? "text-white/85" : "text-[#16241C]"}`}>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`pricing-quote-button-${i}`}
                  className={`mt-8 block rounded-full py-3.5 text-center text-sm font-bold transition-colors duration-300 ${
                    tier.featured
                      ? "bg-[#F4BE36] text-[#043B1C] hover:bg-white"
                      : "bg-[#043B1C] text-white hover:bg-[#F4BE36] hover:text-[#043B1C]"
                  }`}
                >
                  Get My Quote
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mx-auto mt-10 max-w-3xl text-center text-xs leading-[1.7] text-[#5B6B60]" data-testid="pricing-disclaimer">
            Starting prices apply to standard residential properties. Final pricing depends on property size, leaf volume, access and gutter height. Heavily treed properties and difficult-access gutters may require adjusted pricing. Final price is confirmed before service.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
